import Breadcrumb from 'components/atoms/Breadcrumb';
import PlanActions, { timePeriodDropdownList } from 'components/molecules/TradePromoOptimization/PlanActions';
import { UserContext } from 'contexts/ConfigContext';
import { TPODataContext, initialPlanEditMetaData } from 'contexts/TPOContext';
import { useRef, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import {
    CheckCircleFilled,
    LogoutOutlined,
    LoadingOutlined,
    CloseCircleFilled,
    SyncOutlined,
    DownOutlined,
    CheckCircleTwoTone,
    CloseCircleOutlined,
    UpOutlined,
    RightOutlined,
} from '@ant-design/icons';
import {
    calculateContainerHeight,
    compareUsers,
    fetchTokenFromLocalStorage,
    generateErrorMsg,
    getConfig,
    sendNotification,
    transformFiltersV3,
} from 'utils/helperMethods';
import EventsTableContainer from 'components/molecules/TradePromoOptimization/EventsTableContainer';
import AddOrEditPromoModal from 'components/molecules/TradePromoOptimization/AddPromo/AddOrEditPromo';
import AddOrEditEventModal from 'components/molecules/TradePromoOptimization/Modals/AddOrEditEventModal';
import PlanOverviewContainer from 'components/molecules/TradePromoOptimization/Modals/PlanOverviewContainer';
import { queryClient } from 'services/reactQueryService';
import ImportEventFromPlanModal from 'components/molecules/TradePromoOptimization/Modals/ImportEventFromPlanModal';
import ImportEventFromEventLibraryModal from 'components/molecules/TradePromoOptimization/Modals/ImportEventFromEventLibraryModal';
import TabSwitchComponent from 'components/atoms/TabSwitch/TabSwitch';
import { DimensionDropdownType, tabsType } from 'types/pricingSimulator';
import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';
import { Alert, Button, Spin, Switch, Tag, Tooltip } from 'antd';
import { antIcon } from 'components/molecules/Filter/FilterApplyButton';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import { INFEASIBLE_SOLUTION_KEYWORDS } from 'constants/tpo';
import { OptimizationPollingStatus, InfeasibilityPollingStatus } from 'types/tradePromoOptimization';
import { useFetchCalenderData } from 'hooks/tradePromoOptimization/events/useFetchCalenderData';
import { colorConstants } from 'config/colorConstants';
import dayjs from 'dayjs';
import TimerDisplay from 'components/molecules/common/TimerDisplay';
import ErrorPopover from 'components/molecules/TradePromoOptimization/ErrorPopOver';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import _ from 'lodash';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { getOptimizationStatusIconColor } from 'utils/optimizationStatusUtils';
import PublishPlanModal from 'components/molecules/TradePromoOptimization/Modals/PublishPlanModal';
import InfeasiblePlanDrawer from 'components/molecules/TradePromoOptimization/Modals/InfeasiblePlanDrawer';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { trackEventMixpanel } from 'utils/mixpanel';
import { TPO_PLAN_DETAILS_VIEWED } from 'utils/mixpanelEventNames';
const mainContainerHeight = calculateContainerHeight(1, 16 * 3);

const mainTabList = [
    { key: 'plan', label: 'Plan' },
    // { key: 'budget', label: 'Budget' },
];

export const tpmTpoTabComponent = [
    { key: 'tpm', label: 'TPM' },
    { key: 'tpo', label: 'TPO' },
];

export const addEventDropdownList = [
    { label: 'Add new Event', key: 'add_event' },
    { label: 'Import from Plan', key: 'import_plan' },
    // { label: 'Import from Event Library', key: 'import_event_library' },
] as const;

export const addEventDropdownListEventLibrary = [
    { label: 'Add new Event', key: 'add_event' },
    { label: 'Import from Plan', key: 'import_plan' },
] as const;

const publishStatusConfig = [
    {
        key: 'published',
        icon: <CheckCircleTwoTone className="text-[14px] align-middle" twoToneColor="#52c41a" />,
        color: 'green',
        label: 'Published',
        match: (status: string | null) => status === 'published',
    },
    {
        key: 'publish_in_progress',
        icon: <SyncOutlined spin className="text-[14px] align-middle" color="blue" />,
        color: 'processing',
        label: 'Publishing in progress',
        match: (status: string | null, isPublishing: string) =>
            isPublishing === 'INPROGRESS' || status === 'publish_in_progress',
    },
    {
        key: 'failed_to_publish',
        icon: <CloseCircleOutlined className="text-[14px] align-middle" color="red" />,
        color: 'red',
        label: 'Failed',
        match: (status: string | null) => status === 'failed_to_publish',
    },
    {
        key: 'publish_failed',
        icon: <CloseCircleOutlined className="text-[14px] align-middle" color="red" />,
        color: 'red',
        label: 'Rejected',
        match: (status: string | null) => status === 'publish_failed',
    },
];

const approvalStatusConfig = [
    {
        key: 'approval_pending',
        icon: <DownOutlined className="text-[14px] align-middle" color="yellow" />,
        color: 'warning',
        label: 'Pending approval',
        match: (status: string | null, isSendingForApproval: string) =>
            isSendingForApproval === 'INPROGRESS' || status === 'approval_pending',
    },
    {
        key: 'approved',
        icon: <CheckCircleTwoTone className="text-[14px] align-middle" twoToneColor="#52c41a" />,
        color: 'green',
        label: 'Approved',
        match: (status: string | null) => status === 'approved',
    },
    {
        key: 'failed_to_send_approval',
        icon: <CloseCircleOutlined className="text-[14px] align-middle" color="red" />,
        color: 'red',
        label: 'Failed to approve',
        match: (status: string | null) => status === 'failed_to_send_approval',
    },
    {
        key: 'rejected',
        icon: <CloseCircleOutlined className="text-[14px] align-middle" color="red" />,
        color: 'red',
        label: 'Approval Rejected',
        match: (status: string | null) => status === 'rejected',
    },
];

type StatusInfo = {
    approval_status: string | null;
    approval_request_ts: string | null;
    approval_log: string | null;
    publish_status: string | null;
    publish_time: string | null;
    publish_log: string | null;
};

export const mainSectionId = 'update-view-tpo-main-section';
const UpdateOrViewTPO = () => {
    const [triggerApiCall, setTriggerApiCall] = useState(0); //dummy state to re trigger api call in react query
    const [openPromoModal, setOpenPromoModal] = useState<{
        eventId: null | string;
        eventPromoId: null | string;
        open: boolean;
    }>({
        eventId: null,
        eventPromoId: null,
        open: false,
    });
    const [openSessionEndedConfirmationModal, setSessionEndedConfirmationModal] = useState({ title: '', open: false });
    const [openInfeasibleDrawer, setOpenInfeasibleDrawer] = useState(false);

    const [openEventModal, setOpenEventModal] = useState<{ eventId: null | string; open: boolean }>({
        eventId: null,
        open: false,
    });

    const {
        breadcrumbItems,
        selectedPlanInfo,
        isTpmTab,
        setTpmTpoActiveTab,
        tpmTpoActiveTab,
        kpiConfigData,
        resetCurrentState,
        eventViewType,
        showCalenderChanges,
        planMetaDataLoading,
        showOptimizedPlanCalender,
        setShowOptimizedPlanCalender,
        pollingStatus,
        optimizedPlanId,
        viewMode,
        setPlanEditMetaData,
        isOptimizationInProgress,
        optimizationError,
        setOptimizationError,
        setEventViewType,
        setFinalCalenderStatues,
        infeasibilityPollingStatus,
        infeasibilityData,
        promoConfigDictionary,
    } = useContext(TPODataContext);

    const isInfeasibleSolution =
        pollingStatus === OptimizationPollingStatus.COMPLETED &&
        INFEASIBLE_SOLUTION_KEYWORDS.some((keyword) =>
            optimizationError?.title?.toLowerCase()?.includes(keyword.toLowerCase()),
        );

    const { appConfig } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const enableEditMode = tpoConfig?.enableEditMode;
    const kpiViewOptions = tpoConfig?.kpiViewOptions;
    const { calendarFilterKeys, calendarFilterDimensionInfo } = tpoConfig;
    const [openKpiViewPopup, setOpenKpiViewPopup] = useState(false);
    const isCalenderView = eventViewType === 'calender';
    //  kpi table variable
    const [kpiVariable, setKpiVariable] = useState<any>({
        group_by: [],
        time_period: timePeriodDropdownList?.[0]?.value,
        event_type: 'tpo',
        kpi_view: kpiViewOptions?.[0]?.key,
        current_date: dayjs().unix(),
    });

    useEffect(() => () => setOptimizationError(null), []);

    useEffect(() => {
        if (selectedPlanInfo) {
            trackEventMixpanel(TPO_PLAN_DETAILS_VIEWED, {
                ...(selectedPlanInfo ?? {}),
            });
        }
    }, [selectedPlanInfo]);

    const [openImportEventModal, setOpenImportEventModal] = useState<boolean>(false);
    const [openImportEventLibraryModal, setOpenImportEventLibraryModal] = useState<boolean>(false);
    const [openSyncWithTpmConfirmationModal, setOpenSyncWithTpmConfirmationModal] = useState<boolean>(false);
    const [openOptimizationSyncConfirmationModal, setOpenOptimizationSyncConfirmationModal] = useState<boolean>(false);
    const [openSavePlanConfirmationModal, setOpenSavePlanConfirmationModal] = useState<boolean>(false);
    const [openPublishModal, setOpenPublishPlan] = useState(false);
    const [activeTab, setActiveTab] = useState(mainTabList[0]);
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});
    const [availableFilters, setAvailableFilters] = useState<DimensionDropdownType[]>([]);
    const [applyFilterObject, setApplyFilterObject] = useState<any>({});
    const [allCheckedAttribute, setAllCheckedAttribute] = useState<any>({});
    const [allSelectedValueForAllAttribute, setAllSelectedValueForAllAttribute] = useState<any>({});
    const [isPublishing, setIsPublishing] = useState('');
    const [isSendingForApproval, setIsSendingForApproval] = useState('');
    const [finalAllCheckedAttribute, setFinalAllCheckedAttribute] = useState<any>({}); // Persistent storage for selected filter values across all tabs
    const [finalAllSelectedValueForAllAttribute, setFinalAllSelectedValueForAllAttribute] = useState<any>({}); // Persistent storage for "Select All" states across all tabs
    const [finalApplyFilterObject, setFinalApplyFilterObject] = useState<any>({}); // Persistent storage for applied filter configurations across all tabs
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [isPublishFailedExpanded, setIsPublishFailedExpanded] = useState(false);
    const [publishablePromos, setPublishablePromos] = useState<any[]>([]);

    //calender variables
    const [calenderVariable, setCalenderVariable] = useState<any>({});
    const [selectedCalendarColumns, setSelectedCalendarColumns] = useState<string[]>([]);
    const [calenderSearchKey, setCalenderSearchKey] = useState('');
    const [kpiSearchKey, setKpiSearchKey] = useState('');

    const mainSectionHeight = useRef<number | null>(0);
    // prevent unnecessary api calls by checking the last status
    const lastStatusRef = useRef<{ publish_status: string | null; approval_status: string | null }>({
        publish_status: null,
        approval_status: null,
    });

    //this hook will give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: mainSectionId,
        height: mainSectionHeight.current,
        buffer: 120, //for padding at the bottom, change to 16 once testing is done
        triggerDummyRender: true,
    });

    //add event button click handler
    function addEventButtonClickHandler(from: typeof addEventDropdownList[number]['key']) {
        if (from === 'add_event') return setOpenEventModal({ eventId: null, open: true });
        if (from === 'import_plan') return setOpenImportEventModal(true);
        if (from === 'import_event_library') return setOpenImportEventLibraryModal(true);
    }

    //calender api
    const {
        loading: calenderDataLoading,
        error: calenderDataError,
        data: calenderData,
        count: calenderDataCount = 0,
        deletedCount: calenderDataDeletedCount = 0,
    } = useFetchCalenderData({
        method: 'post',
        queryConfig: {
            enabled:
                isCalenderView &&
                Boolean(calenderVariable?.plan_id && Object.keys(calenderVariable?.filters)?.length > 0),
        },
        url: tradePromoOptimizationUrls.getCalenderDataUrl,
        variables: calenderVariable,
        queryKey: ['tpo-event-data', 'tpo-calender-data', calenderVariable],
        showChanges: showCalenderChanges,
    });

    const { mutate: syncWithTpm, loading: syncWithTpmLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.syncTpoUrl}/${selectedPlanInfo?.planId}`,
        method: 'delete',
    });

    const { mutate: syncOptimizationWithTpo, loading: syncOptimizationWithTpoLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getSyncOptimizationToTpoUrl}/${optimizedPlanId}`,
        method: 'get',
    });

    const { mutate: releasePlanEditFn, loading: releasePlanEditLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/${selectedPlanInfo?.planId}/edit/release`,
        method: 'post',
    });
    const { mutate: editPlanExtendFn, loading: editPlanExtendLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/${selectedPlanInfo?.planId}/edit/extend`,
        method: 'post',
    });
    const { mutate: getPublishPlanDataFn, loading: getPublishPlanDataLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getPublishablePromos}/${selectedPlanInfo?.planId}`,
        method: 'get',
    });

    const resetFilterObject = () => {
        const allSelectedObj: any = {};
        const selectAllObj: any = {};
        const newApplyFilterObj: any = {};
        calendarFilterKeys.forEach((filter: any) => {
            allSelectedObj[filter.value] = new Set();
            newApplyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(calendarFilterKeys);
        setAllCheckedAttribute(allSelectedObj);
        setApplyFilterObject(_.cloneDeep(newApplyFilterObj));
        setAllSelectedValueForAllAttribute(selectAllObj);
    };

    const onFilterSaveClickHandler = (resultObj: any) => {
        setAllCheckedAttribute(resultObj.allCheckedAttribute);
        setAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setApplyFilterObject(resultObj.applyFilterObject);
        setCalenderVariable({});
    };

    const commonVariables = useMemo(() => {
        if (selectedFilter?.value) {
            const updatedFilters = transformFiltersV3(tempApplyFilterObject, calendarFilterKeys, selectedFilter.value);

            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
                plan_id: showOptimizedPlanCalender ? optimizedPlanId : selectedPlanInfo?.planId,
                is_tpm_plan: selectedPlanInfo?.isTpmPlan,
                is_tpm_tab: isTpmTab,
            };
        }
    }, [selectedFilter, selectedPlanInfo?.planId, selectedPlanInfo?.isTpmPlan, isTpmTab, optimizedPlanId]);

    useEffect(() => {
        resetFilterObject();
    }, []);

    const { mutate: publishPlan, loading: publishPlanLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/publish`,
        method: 'post',
    });

    const { mutate: sendForApproval, loading: sendForApprovalLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/approval`,
        method: 'post',
    });

    const { mutate: getUpdates, loading: getUpdatesLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/updates/${selectedPlanInfo?.planId}`,
        method: 'get',
    });

    useEffect(() => {
        // setting default kpi list based on event view type
        const list = Object.keys(kpiConfigData ?? {})
            .filter((configKey) => {
                const availableFor = kpiConfigData?.[configKey]?.availableFor || [];
                return availableFor.includes(eventViewType);
            })
            .map((configKey) => {
                return {
                    label:
                        eventViewType === 'promo_metrics'
                            ? kpiConfigData?.[configKey]['label']
                            : kpiConfigData?.[configKey]['planLabel'],
                    value: configKey,
                    isSelected: kpiConfigData?.[configKey]?.showByDefault || false,
                };
            });
        // on the initial load, we want to show only showByDefault kpis
        setKpiVariable((prev: any) => ({
            ...prev,
            kpi_list: list?.filter((item) => item?.isSelected)?.map((item) => item?.value),
        }));
    }, [kpiConfigData, eventViewType, appConfig]);

    useEffect(() => {
        if (!calenderData?.statues) return;
        const { publish_status, approval_status } = calenderData.statues;
        setFinalCalenderStatues(calenderData?.statues);

        // Update the ref with current statuses
        lastStatusRef.current = {
            publish_status: publish_status,
            approval_status: approval_status,
        };

        if (publish_status === 'publish_in_progress') setIsPublishing('INPROGRESS');
        if (['failed_to_publish', 'publish_failed'].includes(publish_status)) setIsPublishing('FAILED');
        if (publish_status === 'published') setIsPublishing('PUBLISHED');
        if (approval_status === 'approval_pending') setIsSendingForApproval('INPROGRESS');
        if (['failed_to_send_approval', 'rejected'].includes(approval_status)) setIsSendingForApproval('FAILED');
    }, [calenderData?.statues]);

    useEffect(() => {
        if (!selectedPlanInfo?.planId) return;
        if (isTpmTab) return;

        getUpdatesFn();

        const interval = setInterval(() => {
            getUpdatesFn();
        }, 30000);
        return () => clearInterval(interval);
    }, [selectedPlanInfo?.planId, isTpmTab, isPublishing, isSendingForApproval]);

    function onContinueClickHandler() {
        if (editPlanExtendLoading) return;
        const payload = {
            user_id: localStorage.getItem('email'),
        };
        editPlanExtendFn(payload, {
            onSuccess: (data) => {
                //if user is same, then it means that edit rights are acquired
                if (compareUsers(data?.data?.edit_lock?.editing_user)) {
                    sendNotification('success', tpoNotificationMessages.PLAN_EDIT_EXTEND_SUCCESS);
                } else {
                    sendNotification(
                        'error',
                        `User ${data?.data?.edit_lock?.editing_user} is currently editing this plan`,
                    );
                }
                setPlanEditMetaData({
                    editingUser: data?.data?.edit_lock?.editing_user,
                    sessionExpiresInSecs: data?.data?.edit_lock?.session_expires_in_sec,
                    startedAt: data?.data?.edit_lock?.started_at,
                    planId: data?.data?.plan_id,
                });
                setSessionEndedConfirmationModal({ open: false, title: '' });
            },
            onError: (error: any) => {
                const errorMsg = generateErrorMsg(
                    String(error?.response?.data?.detail || ''),
                    ErrorMessages.GENERALIZED_ERROR_MSG,
                );
                //show error message
                sendNotification('error', errorMsg);
            },
        });
    }

    function exitEditModeClickHandler() {
        if (releasePlanEditLoading) return;
        const payload = {
            user_id: localStorage.getItem('email'),
        };
        releasePlanEditFn(payload, {
            onSuccess: (data) => {
                //if user is same, then it means that edit rights are acquired
                sendNotification('success', tpoNotificationMessages.PLAN_EDIT_RELEASE_SUCCESS);
                //remove calender api from cache
                console.log('removing calender api from cache');
                queryClient.removeQueries({
                    queryKey: ['tpo-event-data', 'tpo-calender-data'],
                    exact: false,
                });
                setPlanEditMetaData({
                    ...initialPlanEditMetaData,
                });
            },
            onError: (error: any) => {
                const errorMsg = generateErrorMsg(
                    String(error?.response?.data?.detail || ''),
                    ErrorMessages.GENERALIZED_ERROR_MSG,
                );
                //show error message
                sendNotification('error', errorMsg);
                setPlanEditMetaData({
                    ...initialPlanEditMetaData,
                });
            },
        });
    }

    const getPublishPlanData = () => {
        if (getPublishPlanDataLoading) return;
        setOpenPublishPlan(true);
        getPublishPlanDataFn(
            {},
            {
                onSuccess: (data: any) => {
                    setPublishablePromos(data?.data);
                },
                onError: () => {
                    sendNotification('error', 'Something went Wrong!');
                },
            },
        );
    };

    function onPublishPlan() {
        if (publishPlanLoading) return;
        setConfirmLoading(true);
        const payload = {
            plan_id: selectedPlanInfo?.planId,
            user_email_id: localStorage.getItem('email'),
        };
        publishPlan(payload, {
            onSuccess: (data: any) => {
                if (data?.data?.error) {
                    sendNotification('error', data?.data?.error);
                    setOpenPublishPlan(false);
                } else {
                    setTriggerApiCall(triggerApiCall + 1);
                    setOpenPublishPlan(false);
                    setIsPublishing('INPROGRESS');
                    exitEditModeClickHandler();
                }
                setConfirmLoading(false);
            },
            onError: () => {
                setIsPublishing('FAILED');
                setOpenPublishPlan(false);
                setConfirmLoading(false);
                sendNotification('error', 'Something went Wrong!');
            },
        });
    }

    function onSendForApproval() {
        if (sendForApprovalLoading) return;
        const payload = {
            plan_id: selectedPlanInfo?.planId,
        };
        sendForApproval(payload, {
            onSuccess: () => {
                setTriggerApiCall(triggerApiCall + 1);
                setIsSendingForApproval('INPROGRESS');
            },
            onError: () => {
                setTriggerApiCall(triggerApiCall + 1);
                setIsSendingForApproval('FAILED');
            },
        });
    }

    const handleStatusUpdate = (statues: any) => {
        const currentPublishStatus = statues?.publish_status;
        const currentApprovalStatus = statues?.approval_status;

        const isPublishStatusFinal = ['published', 'failed_to_publish', 'publish_failed'].includes(
            currentPublishStatus,
        );
        const isApprovalStatusFinal = ['approved', 'failed_to_send_approval', 'rejected'].includes(
            currentApprovalStatus,
        );

        if (
            lastStatusRef.current.publish_status == currentPublishStatus &&
            lastStatusRef.current.approval_status == currentApprovalStatus
        ) {
            return;
        }

        const publishStatusChanged = lastStatusRef.current.publish_status !== currentPublishStatus;
        const approvalStatusChanged = lastStatusRef.current.approval_status !== currentApprovalStatus;
        const isInitialCall =
            lastStatusRef.current.publish_status === null && lastStatusRef.current.approval_status === null;

        if (
            publishStatusChanged ||
            approvalStatusChanged ||
            isInitialCall ||
            isPublishStatusFinal ||
            isApprovalStatusFinal
        ) {
            lastStatusRef.current = { publish_status: currentPublishStatus, approval_status: currentApprovalStatus };

            if (isPublishStatusFinal || isApprovalStatusFinal) {
                setTriggerApiCall((prev) => prev + 1);
            }
        }
    };

    function getUpdatesFn() {
        if (isPublishing === 'INPROGRESS' || isSendingForApproval === 'INPROGRESS') {
            getUpdates(
                {},
                {
                    onSuccess: (data: any) => handleStatusUpdate(data?.data),
                    onError: () => {
                        console.error('error in getting updates');
                    },
                },
            );
        }
    }

    const calenderDataObject = {
        calenderDataLoading,
        calenderDataError,
        calenderData,
        calenderDataCount,
        calenderDataDeletedCount,
    };

    const pendingApprovalItems = useMemo(() => {
        if (calenderDataObject?.calenderData?.result?.length && !calenderDataLoading) {
            return calenderDataObject?.calenderData?.result?.filter(
                (item: any) => item?.promoStatus?.toLowerCase() === 'pending approval',
            );
        }
        return [];
    }, [calenderDataObject?.calenderData?.result, calenderDataLoading]);

    const failedToPublishPromoItems = useMemo(() => {
        if (calenderDataObject?.calenderData?.result?.length && !calenderDataLoading) {
            return calenderDataObject?.calenderData?.result?.filter(
                (item: any) => item?.publish_status?.toLowerCase() === 'failed_to_publish',
            );
        }
        return [];
    }, [calenderDataObject?.calenderData?.result, calenderDataLoading]);

    const calenderStatues: StatusInfo = useMemo(() => {
        if (calenderDataObject?.calenderData?.result?.length) {
            if (calenderDataObject?.calenderData?.statues) {
                return calenderDataObject.calenderData.statues;
            }
        }
    }, [calenderDataObject?.calenderData?.result]);

    const renderPublishStatus = (isTpmTab: any, status: string | null, time: string | null, isPublishing: string) => {
        if (isTpmTab) return null;
        const config = publishStatusConfig.find((cfg) => cfg.match(status, isPublishing));
        if (!config) return null;

        let labelContent: any = config.label;
        if (config.label === 'Published' && time) {
            const publishedDate = dayjs(time);
            const now = dayjs();
            let timeStr = '';
            timeStr = publishedDate.format('DD MMM YYYY');
            labelContent = (
                <span className="flex items-center">
                    {config.label}
                    <span className="ml-2 text-gray-500">({timeStr})</span>
                </span>
            );
        } else {
            labelContent = <span className="flex items-center">{config.label}</span>;
        }

        return (
            <div className="flex items-center justify-center">
                <Tag
                    icon={config.icon}
                    color={config.color}
                    className="!py-[5px] px-5 rounded font-normal text-[14px] !flex items-center justify-center !rounded-md !m-0"
                >
                    <div className="flex items-center justify-between">
                        {labelContent}
                        {config.label === 'Failed to publish' || config.label === 'Failed' ? (
                            <div
                                className="flex items-center justify-center ml-2"
                                onClick={() => setIsPublishFailedExpanded(!isPublishFailedExpanded)}
                            >
                                {isPublishFailedExpanded ? <UpOutlined /> : <RightOutlined />}
                            </div>
                        ) : null}
                    </div>
                </Tag>
            </div>
        );
    };

    const renderApprovalStatus = (
        isTpmTab: any,
        status: string | null,
        time: string | null,
        isSendingForApproval: string,
    ) => {
        if (isTpmTab) return null;
        const config = approvalStatusConfig.find((cfg) => cfg.match(status, isSendingForApproval));
        if (!config) return null;

        let labelContent: any = config.label;
        if (config.label === 'Approved' && time) {
            const publishedDate = dayjs(time);
            const now = dayjs();
            let timeStr = '';
            timeStr = publishedDate.format('DD MMM YYYY');
            labelContent = (
                <span className="flex items-center">
                    {config.label}
                    <span className="ml-2 text-gray-500">({timeStr})</span>
                </span>
            );
        } else {
            labelContent = <span className="flex items-center">{config.label}</span>;
        }

        return (
            <div className="flex items-center justify-center">
                <Tag
                    icon={pendingApprovalItems?.length ? config.icon : null}
                    color={config.color}
                    className="!py-[5px] px-5 rounded font-normal text-[14px] !mr-0 !flex items-center justify-center !rounded-md"
                >
                    {labelContent}
                </Tag>
            </div>
        );
    };

    const renderDraftStatus = (status: string | null, approvalStatus: string | null) => {
        if (approvalStatus !== 'draft') return null;
        return (
            <div className="flex items-center justify-center">
                <Tag
                    color="default"
                    className="!py-[5px] px-5 rounded font-normal text-[14px] !flex items-center justify-center !rounded-md !m-0"
                >
                    <span className="flex items-center">{(!status || status) === 'unknown' ? 'Draft' : status}</span>
                </Tag>
            </div>
        );
    };

    const updateFilterObject = (filterKeys: any[]) => {
        // Initialize temporary objects to build the new filter state
        const tempAllCheckedAttribute: any = {}; // Selected filter values for current tab
        const tempSelectAllObj: any = {}; // "Select All" checkbox states for current tab
        const tempApplyFilterObj: any = {}; // Applied filter configurations for current tab

        // STEP 1: Preserve current filter state in persistent storage
        // Before switching tabs, save the current filter selections to final objects
        // This acts as an in-memory cross-tab storage mechanism
        Object.keys(allCheckedAttribute)?.forEach((key: string) => {
            finalAllCheckedAttribute[key] = _.cloneDeep(allCheckedAttribute[key]);
            finalAllSelectedValueForAllAttribute[key] = _.cloneDeep(allSelectedValueForAllAttribute[key]);
            finalApplyFilterObject[key] = _.cloneDeep(applyFilterObject[key]);
        });

        // STEP 2: Build new filter state with persistence priority
        // For each filter key available in the new tab, apply this priority:
        // 1st: Use current tab state (if available)
        // 2nd: Use persistent cross-tab state (if previously saved)
        // 3rd: Create default empty state
        filterKeys?.forEach((filter: any) => {
            // Handle allCheckedAttribute (selected filter values)
            if (filter.value in allCheckedAttribute) {
                // Priority 1: Use current tab's existing selections
                tempAllCheckedAttribute[filter.value] = allCheckedAttribute[filter.value];
            } else if (filter.value in finalAllCheckedAttribute && finalAllCheckedAttribute[filter.value]?.size) {
                // Priority 2: Restore from cross-tab persistent storage if it has values
                tempAllCheckedAttribute[filter.value] = finalAllCheckedAttribute[filter.value];
            } else {
                // Priority 3: Initialize with empty Set and save to persistent storage
                tempAllCheckedAttribute[filter.value] = new Set();
                finalAllCheckedAttribute[filter.value] = new Set();
            }

            // Handle applyFilterObject (applied filter configurations)
            if (filter.value in applyFilterObject) {
                // Priority 1: Use current tab's existing applied filters
                tempApplyFilterObj[filter.value] = applyFilterObject[filter.value];
            } else if (
                filter.value in finalApplyFilterObject &&
                Object.keys(finalApplyFilterObject[filter.value] ?? {}).length > 0
            ) {
                // Priority 2: Restore from cross-tab persistent storage if it has configurations
                tempApplyFilterObj[filter.value] = finalApplyFilterObject[filter.value];
            } else {
                // Priority 3: Initialize with empty object and save to persistent storage
                tempApplyFilterObj[filter.value] = {};
                finalApplyFilterObject[filter.value] = {};
            }

            // Handle allSelectedValueForAllAttribute ("Select All" states)
            if (filter.value in allSelectedValueForAllAttribute) {
                // Priority 1: Use current tab's existing "Select All" state
                tempSelectAllObj[filter.value] = allSelectedValueForAllAttribute[filter.value];
            } else if (
                filter.value in finalAllSelectedValueForAllAttribute &&
                finalAllSelectedValueForAllAttribute[filter.value]
            ) {
                // Priority 2: Restore from cross-tab persistent storage if it was selected
                tempSelectAllObj[filter.value] = finalAllSelectedValueForAllAttribute[filter.value];
            } else {
                // Priority 3: Initialize with false and save to persistent storage
                tempSelectAllObj[filter.value] = false;
                finalAllSelectedValueForAllAttribute[filter.value] = false;
            }
        });

        // STEP 3: Update all state variables with the new computed values
        setAvailableFilters(filterKeys ?? []); // Set current tab's available filters

        // Update current tab filter states
        setAllCheckedAttribute(_.cloneDeep(tempAllCheckedAttribute));
        setApplyFilterObject(_.cloneDeep(tempApplyFilterObj));
        setAllSelectedValueForAllAttribute(_.cloneDeep(tempSelectAllObj));

        // Update persistent cross-tab storage
        setFinalAllCheckedAttribute(_.cloneDeep(finalAllCheckedAttribute));
        setFinalAllSelectedValueForAllAttribute(_.cloneDeep(finalAllSelectedValueForAllAttribute));
        setFinalApplyFilterObject(_.cloneDeep(finalApplyFilterObject));
    };

    const iconColorByStatusName = useMemo(() => {
        return getOptimizationStatusIconColor(optimizationError?.title);
    }, [optimizationError?.title]);

    // const isPublishIncluded = useMemo(() => {
    //     return calenderDataObject?.calenderData?.result?.some((item: any) => item?.is_publishable);
    // }, [calenderDataObject?.calenderData?.result]);

    const filterDataParser = (data: any) => {
        return data?.map((item: any) => {
            switch (selectedFilter?.value) {
                case 'tactic_type_id':
                    return {
                        label: promoConfigDictionary?.tactics[item],
                        value: item,
                    };
                case 'promotion_type_id':
                    return {
                        label: promoConfigDictionary?.promotions[item],
                        value: item,
                    };
                default:
                    return {
                        label: item,
                        value: item,
                    };
            }
        });
    };

    return (
        <section style={{ height: mainContainerHeight }} id="plans-details-view-update-page">
            <div className="flex items-center justify-between gap-4">
                <Breadcrumb style={{ marginBottom: 10, flexShrink: 0 }} items={breadcrumbItems} />
                <PlanOverviewContainer />
            </div>
            <div className="bg-white p-4 border rounded border-borderLinePurpuleAlt">
                <div className="flex items-center justify-between">
                    {/* <TileButton
                        onClick={(item: any) => {
                            setActiveTab(item);
                        }}
                        style={{ width: 'min-content', marginBottom: 8 }}
                        activeKey={activeTab?.key}
                        data={mainTabList}
                    /> */}
                </div>
                <div className="flex justify-between gap-2 items-center">
                    <div className="flex gap-2 items-center">
                        <div className="flex gap-2 justify-center items-center">
                            {showOptimizedPlanCalender && pollingStatus === OptimizationPollingStatus.COMPLETED && (
                                <>
                                    <ErrorPopover
                                        errorIcon={
                                            <Tooltip showArrow={false} placement="topRight" title="Optimized Plan">
                                                <CheckCircleFilled
                                                    style={{
                                                        fontSize: 18,
                                                        color: iconColorByStatusName || colorConstants.limeGreen,
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            </Tooltip>
                                        }
                                        promoPopupErrors={[
                                            {
                                                title: optimizationError?.title || '',
                                                description: [optimizationError?.message || ''],
                                            },
                                        ]}
                                        disableErrorCount={true}
                                        headerClassName="text-grayAlt"
                                    />
                                </>
                            )}
                            {isOptimizationInProgress && (
                                <Tooltip showArrow={false} placement="topRight" title="Optimization in progress">
                                    <Spin
                                        indicator={
                                            <LoadingOutlined
                                                style={{ fontSize: 18, color: colorConstants.cyan }}
                                                spin
                                            />
                                        }
                                    />
                                </Tooltip>
                            )}
                            {pollingStatus === OptimizationPollingStatus.ERROR && (
                                <>
                                    <ErrorPopover
                                        errorIcon={
                                            <CloseCircleFilled
                                                style={{
                                                    fontSize: 18,
                                                    color: colorConstants.redAlt,
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        }
                                        promoPopupErrors={[
                                            {
                                                title: optimizationError?.title || '',
                                                description: [optimizationError?.message || ''],
                                            },
                                        ]}
                                        disableErrorCount={true}
                                        headerClassName="text-grayAlt"
                                    />
                                </>
                            )}
                            <h2 className="font-md m-0 font-semibold">{selectedPlanInfo?.planName}</h2>
                        </div>
                        {selectedPlanInfo?.isTpmPlan && (
                            <TabSwitchComponent
                                activeTabKey={tpmTpoActiveTab?.key}
                                onTabChange={function (tab: tabsType): void {
                                    setTpmTpoActiveTab(tab);
                                    setCalenderVariable({});
                                    setEventViewType('calender');
                                    setTriggerApiCall(triggerApiCall + 1);
                                    resetFilterObject();
                                }}
                                tabs={tpmTpoTabComponent}
                            />
                        )}
                        {renderDraftStatus(selectedPlanInfo?.status || '', calenderStatues?.approval_status)}
                        {renderApprovalStatus(
                            isTpmTab,
                            calenderStatues?.approval_status,
                            calenderStatues?.approval_request_ts,
                            isSendingForApproval,
                        )}
                        {renderPublishStatus(
                            isTpmTab,
                            calenderStatues?.publish_status,
                            calenderStatues?.publish_time,
                            isPublishing,
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {pollingStatus === OptimizationPollingStatus.COMPLETED && (
                            <>
                                <div className="flex items-center gap-1">
                                    <span className="font-xs">Original</span>
                                    <Switch
                                        onChange={() => {
                                            setCalenderVariable({});
                                            setShowOptimizedPlanCalender(!showOptimizedPlanCalender);
                                            resetFilterObject();
                                        }}
                                        checked={showOptimizedPlanCalender}
                                    />
                                    <span className="font-xs">Optimized</span>
                                </div>
                                {isInfeasibleSolution && (
                                    <Tooltip
                                        title={
                                            infeasibilityPollingStatus === InfeasibilityPollingStatus.IN_PROGRESS
                                                ? 'Diagnosing infeasibility constraints'
                                                : ''
                                        }
                                        placement="bottom"
                                        color="white"
                                        overlayInnerStyle={{ color: '#333' }}
                                    >
                                        <Button
                                            disabled={
                                                infeasibilityPollingStatus === InfeasibilityPollingStatus.IN_PROGRESS
                                            }
                                            style={{ borderRadius: 4 }}
                                            onClick={() => setOpenInfeasibleDrawer(true)}
                                            type="default"
                                        >
                                            <span className="flex items-center gap-2">
                                                {infeasibilityPollingStatus ===
                                                    InfeasibilityPollingStatus.IN_PROGRESS && (
                                                    <Spin
                                                        indicator={antIconViolet}
                                                        style={{ position: 'relative', top: -3, marginRight: 10 }}
                                                    />
                                                )}
                                                Infeasible Plan Factors{' '}
                                                <RightOutlined style={{ position: 'relative', top: -3 }} />
                                            </span>
                                        </Button>
                                    </Tooltip>
                                )}
                            </>
                        )}

                        {!viewMode && enableEditMode && (
                            <div className="flex items-center gap-2">
                                <div className="">
                                    <TimerDisplay
                                        setSessionEndedConfirmationModal={setSessionEndedConfirmationModal}
                                        openSessionEndedConfirmationModal={openSessionEndedConfirmationModal}
                                    />
                                </div>
                                <div
                                    onClick={() => {
                                        exitEditModeClickHandler();
                                    }}
                                    className="flex gap-2 cursor-pointer items-center text-purpleFont"
                                >
                                    {releasePlanEditLoading ? (
                                        <Spin indicator={antIconViolet} />
                                    ) : (
                                        <LogoutOutlined style={{ fontSize: 15, color: colorConstants.violet }} />
                                    )}
                                    <p> Exit edit mode</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {!isTpmTab && calenderStatues?.approval_status == 'approval_pending' && pendingApprovalItems?.length ? (
                    <Alert
                        description={
                            <div className="flex items-center">
                                {pendingApprovalItems?.map((item: any, idx: number) => (
                                    <Tag key={idx} className="!border !border-[#E0E0E0] !rounded">
                                        <span style={{ fontWeight: 600 }}>{item?.promo}</span>
                                    </Tag>
                                ))}
                            </div>
                        }
                        message={
                            <span className="font-xs">
                                <b>Pending approval</b> ({pendingApprovalItems?.length} promos submitted for approval)
                            </span>
                        }
                        type="warning"
                        showIcon={false}
                        closable
                        className="bg-[#FFFBE6] border border-[#F6D9A7] !my-2 !rounded-lg !px-3 !py-2"
                    />
                ) : null}
                {!isTpmTab &&
                calenderStatues?.publish_status === 'failed_to_publish' &&
                failedToPublishPromoItems?.length &&
                isPublishFailedExpanded ? (
                    <Alert
                        description={
                            <>
                                <div className="flex items-center">
                                    {failedToPublishPromoItems?.map((item: any, idx: number) => (
                                        <Tag key={idx} className="!border !border-[#E0E0E0] !rounded">
                                            <span style={{ fontWeight: 600 }}>{item?.promo}</span>
                                        </Tag>
                                    ))}
                                </div>
                                {JSON.parse(calenderStatues?.publish_log as any)?.map((item: any, idx: number) => {
                                    const excludeIds = failedToPublishPromoItems?.map((promo: any) => promo?.planId);
                                    if (!excludeIds?.includes(item?.promo_id)) {
                                        return (
                                            <div key={idx} className="mt-1">
                                                <span>{item?.error}</span>
                                                <br />
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </>
                        }
                        message={
                            <span className="font-xs">
                                <b>Failed to publish</b> ({failedToPublishPromoItems?.length} promos failed to publish)
                            </span>
                        }
                        type="error"
                        showIcon={false}
                        closable
                        className="bg-[#FFFBE6] border border-[#F6D9A7] !my-2 !rounded-lg !px-3 !py-2"
                    />
                ) : null}
                <PlanActions
                    setOpenKpiViewPopup={setOpenKpiViewPopup}
                    setOpenOptimizationSyncConfirmationModal={setOpenOptimizationSyncConfirmationModal}
                    setCalenderVariable={setCalenderVariable}
                    onViewDropdownChange={() => {
                        setCalenderSearchKey('');
                        setKpiSearchKey('');
                    }}
                    onSearch={(val) => {
                        if (isCalenderView) setCalenderSearchKey(val);
                        else setKpiSearchKey(val);
                    }}
                    disableAddEvent={calenderDataLoading || planMetaDataLoading}
                    kpiVariable={kpiVariable}
                    onDropdownChange={(variables: any) => {
                        setKpiVariable((prev: any) => ({ ...prev, ...variables, offset: 0 }));
                    }}
                    setOpenSavePlanConfirmationModal={setOpenSavePlanConfirmationModal}
                    setOpenSyncWithTpmConfirmationModal={setOpenSyncWithTpmConfirmationModal}
                    addEventButtonClickHandler={addEventButtonClickHandler}
                    onClickPublishPlan={getPublishPlanData}
                    onSendForApproval={onSendForApproval}
                    calenderStatues={calenderStatues}
                    updateFilterObject={updateFilterObject}
                    setKpiVariable={setKpiVariable}
                    selectedCalendarColumns={selectedCalendarColumns}
                    setSelectedCalendarColumns={setSelectedCalendarColumns}
                    calenderDataLoading={calenderDataLoading}
                    applyFilterObject={applyFilterObject}
                    availableFilters={availableFilters}
                    filterComponent={
                        <NewGlobalFilter
                            availableFilters={availableFilters}
                            allCheckedAttribute={allCheckedAttribute}
                            allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                            applyFilterObject={applyFilterObject}
                            resetFilterObject={resetFilterObject}
                            onSubmitHandler={onFilterSaveClickHandler}
                            url={tradePromoOptimizationUrls.getCalendarDataFilterAttributes}
                            selectedFilter={selectedFilter}
                            setSelectedFilter={setSelectedFilter}
                            tempApplyFilterObject={tempApplyFilterObject}
                            setTempApplyFilterObject={setTempApplyFilterObject}
                            apiVariables={commonVariables}
                            filterDimensionInfo={calendarFilterDimensionInfo}
                            showAttributePanelToLeft
                            dataParser={filterDataParser}
                        />
                    }
                />
                <div className="my-3">
                    <FilterRibbon
                        allCheckedAttribute={allCheckedAttribute}
                        allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                        applyFilterObject={applyFilterObject}
                        resetFilterObject={resetFilterObject}
                        onSubmitHandler={(obj: any) => {
                            setAllCheckedAttribute(obj.allCheckedAttribute);
                            setAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                            setApplyFilterObject(obj.applyFilterObject);
                            setCalenderVariable({});
                        }}
                        renderAttribute={(key: string, attributeValue: string | number) => {
                            switch (key) {
                                case 'tactic_type_id':
                                    return (
                                        <span className="py-1.5">
                                            {promoConfigDictionary?.tactics[attributeValue as number]}
                                        </span>
                                    );
                                case 'promotion_type_id':
                                    return (
                                        <span className="py-1.5">
                                            {promoConfigDictionary?.promotions[attributeValue as number]}
                                        </span>
                                    );
                                default:
                                    return <span className="py-1.5">{attributeValue}</span>;
                            }
                        }}
                        filterKeys={calendarFilterKeys}
                        parentClassName="px-0"
                    />
                </div>
            </div>

            {/* MAIN SECTION*/}

            <EventsTableContainer
                openKpiViewPopup={openKpiViewPopup}
                setOpenKpiViewPopup={setOpenKpiViewPopup}
                calenderSearchKey={calenderSearchKey}
                kpiSearchKey={kpiSearchKey}
                calenderVariable={calenderVariable}
                setCalenderVariable={setCalenderVariable}
                calenderDataObject={calenderDataObject}
                kpiVariable={kpiVariable}
                setKpiVariable={setKpiVariable}
                triggerApiCall={triggerApiCall}
                mainSectionHeight={mainSectionHeight}
                setOpenPromoModal={setOpenPromoModal}
                selectedCalendarColumns={selectedCalendarColumns}
                setOpenEventModal={setOpenEventModal}
                setTriggerApiCall={setTriggerApiCall}
                applyFilterObject={applyFilterObject}
                isPublishing={isPublishing}
                availableFilters={availableFilters}
            />

            <AddOrEditPromoModal
                open={openPromoModal?.open}
                onCancel={() => {
                    setOpenPromoModal({ eventId: null, open: false, eventPromoId: null });
                    setTriggerApiCall(triggerApiCall + 1);
                    resetCurrentState();
                }}
                eventId={openPromoModal.eventId}
                eventPromoId={openPromoModal.eventPromoId}
                onSubmitFinish={() => {}}
                disabled={isOptimizationInProgress || viewMode}
            />
            <AddOrEditEventModal
                calenderDataCount={calenderDataCount}
                disabled={isTpmTab || showOptimizedPlanCalender || viewMode || isOptimizationInProgress}
                open={openEventModal.open}
                onCancel={() => {
                    setOpenEventModal({ eventId: null, open: false });
                }}
                eventId={openEventModal.eventId}
                onSubmitFinish={() => {
                    setOpenEventModal({ eventId: null, open: false });
                    setTriggerApiCall(triggerApiCall + 1);
                }}
            />
            <ImportEventFromPlanModal
                open={openImportEventModal}
                onCancel={() => {
                    setOpenImportEventModal(false);
                }}
                onSubmitFinish={() => {
                    setOpenImportEventModal(false);
                    setTriggerApiCall(triggerApiCall + 1);
                }}
            />
            <ImportEventFromEventLibraryModal
                open={openImportEventLibraryModal}
                onCancel={() => {
                    setOpenImportEventLibraryModal(false);
                }}
                onSubmitFinish={() => {
                    setOpenImportEventLibraryModal(false);
                    setTriggerApiCall(triggerApiCall + 1);
                }}
            />
            {/* SYNC WITH TPM */}
            <ConfirmationModal
                open={openSyncWithTpmConfirmationModal}
                onCancel={() => {
                    setOpenSyncWithTpmConfirmationModal(false);
                }}
                onSubmitClick={() => {
                    const payload = {};
                    syncWithTpm(payload, {
                        onSuccess: (data: any) => {
                            sendNotification('success', tpoNotificationMessages.TPM_SYNC_SUCCESS);
                            setTriggerApiCall(triggerApiCall + 1);
                            setOpenSyncWithTpmConfirmationModal(false);
                        },
                        onError: (val: any) => {
                            sendNotification('error', ErrorMessages.GENERALIZED_ERROR_MSG);
                            // onCancel(val);
                        },
                    });
                }}
                okText={syncWithTpmLoading ? <Spin indicator={antIcon} /> : 'Sync'}
                okButtonProps={{ style: { borderRadius: 6 } }}
                loading={syncWithTpmLoading}
                subTitle="Are you sure you want to sync with TPM?"
                title="Sync with TPM"
                description="This will remove the previous version of TPO present in the system"
            />
            {/* SAVE OPTIMIZED PLAN AS ORIGINAL */}
            <ConfirmationModal
                open={openOptimizationSyncConfirmationModal}
                onCancel={() => {
                    setOpenOptimizationSyncConfirmationModal(false);
                }}
                onSubmitClick={() => {
                    const payload = {
                        optimized_plan_id: optimizedPlanId,
                    };
                    syncOptimizationWithTpo(payload, {
                        onSuccess: (data: any) => {
                            sendNotification('success', tpoNotificationMessages.OPTIMIZATION_SYNC_SUCCESS);
                            const currentPath = location.pathname;
                            const pathSegments = currentPath.split('/');
                            // Replace the last segment with newId
                            pathSegments[pathSegments.length - 1] = String(data?.data?.plan_id);
                            const updatedPath = pathSegments.join('/');
                            // Trigger full reload
                            window.location.href = updatedPath;
                            setOpenOptimizationSyncConfirmationModal(false);
                        },
                        onError: (error: any) => {
                            const errorMsg = generateErrorMsg(
                                String(error?.response?.data?.detail),
                                ErrorMessages.GENERALIZED_ERROR_MSG,
                            );
                            //show error message
                            sendNotification('error', errorMsg);
                        },
                    });
                }}
                okText={syncOptimizationWithTpoLoading ? <Spin indicator={antIcon} /> : 'Save'}
                okButtonProps={{ style: { borderRadius: 6 } }}
                loading={syncOptimizationWithTpoLoading}
                subTitle="Are you sure you want to override the existing plan?"
                title="Save as Original"
                description="Saving this will overwrite the original plan and save the optimized plan as the original plan."
            />
            {/* SAVE PLAN */}
            <ConfirmationModal
                open={openSavePlanConfirmationModal}
                onCancel={() => {
                    setOpenSavePlanConfirmationModal(false);
                }}
                onSubmitClick={() => {
                    setOpenSavePlanConfirmationModal(false);
                }}
                okText={false ? <Spin indicator={antIcon} /> : 'Save'}
                okButtonProps={{ style: { borderRadius: 6 } }}
                loading={false}
                subTitle={`Are you sure you want to save ${selectedPlanInfo?.planName}?`}
                title="Save Plan"
                description={`This will save all changes you've made in ${selectedPlanInfo?.planName}`}
            />
            {/* EXTEND PLAN */}
            <ConfirmationModal
                maskClosable={false}
                open={openSessionEndedConfirmationModal?.open}
                onCancel={() => {
                    setSessionEndedConfirmationModal({ open: false, title: '' });
                }}
                onSubmitClick={() => {
                    onContinueClickHandler();
                }}
                okText={editPlanExtendLoading ? <Spin indicator={antIcon} /> : 'Extend'}
                okButtonProps={{ type: 'primary', style: { borderRadius: 6 } }}
                loading={editPlanExtendLoading}
                subTitle={`Would you like to extend the session?`}
                title={openSessionEndedConfirmationModal?.title}
                description=""
            />
            <PublishPlanModal
                open={openPublishModal}
                setOpen={setOpenPublishPlan}
                getPublishPromosLoading={getPublishPlanDataLoading}
                dataObject={publishablePromos}
                onPublishPlan={onPublishPlan}
                confirmLoading={confirmLoading}
            />
            <InfeasiblePlanDrawer
                open={openInfeasibleDrawer}
                onClose={() => setOpenInfeasibleDrawer(false)}
                relaxedConstraints={infeasibilityData || []}
                topOffset={65}
            />
        </section>
    );
};

export default UpdateOrViewTPO;

const antIconViolet = <LoadingOutlined style={{ fontSize: 16, color: colorConstants.violet }} spin />;
